declare const process: {
  env: {
    REACT_APP_API_URL?: string;
  };
};

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

import { PlantUptimeDetails } from '../types';

export interface AssetOwner {
  id: number;
  name: string;
  code: string;
  owner_type: string;
  sites_count: number;
}

export interface Site {
  id: number;
  name: string;
  code: string;
  site_type: string;
  asset_owner: number;
  asset_owner_name: string;
}

export interface Location {
  id: number;
  name: string;
  code: string;
  site: number;
  site_name: string;
  site_code: string;
  building: string;
}

export interface Equipment {
  id: number;
  name: string;
  equipment_number: string;
  equipment_id?: string;
  kks_code?: string;
  site_name: string;
  site_code: string;
  unit_name?: string;
  subsystem_name?: string;
  equipment_unit_name?: string;
  hierarchy_level: string;
  status: string;
  criticality: string;
  requires_isolation: boolean;
  energy_sources_count: number;
  has_high_risk_energy: boolean;
  full_location?: string;
  energizable?: boolean;
}

export interface EnergySource {
  id: number;
  energy_type: string;
  source_name: string;
  voltage_level: string;
  pressure_level: string;
  hazard_level: string;
  isolation_required: boolean;
}

export interface IsolationPoint {
  id: number;
  isolation_type: string;
  isolation_capability: string;
  sequence_number: number;
  is_primary: boolean;
  is_backup: boolean;
  requires_key: boolean;
  key_location: string;
  isolation_equipment_name: string;
  isolation_equipment_kks_code: string;
  operation_time_minutes: number;
  verification_method: string;
}

export interface CurrentValuesDisplayConfig {
  current_values_box: {
    show: boolean;
    display_mode: 'large_buttons' | 'small_buttons' | 'table' | 'compact_table';
  };
  plant_image_overlays: {
    show: boolean;
    display_mode: 'status_icon' | 'minimal_text' | 'compact_button' | 'value_only' | 'badge' | 'gauge';
    show_tooltips: boolean;
    tooltip_content: 'name_value' | 'name_description_value' | 'full_details';
  };
  alarm_indicators: {
    show: boolean;
    style: 'icon' | 'color_border' | 'background_color' | 'pulse_animation';
  };
}

export interface SiteDashboardData {
  site_id: number;
  site_name: string;
  asset_owner: {
    id: number;
    name: string;
    code: string;
    logo?: string;
  };
  statistics: {
    total_equipment: number;
    active_permits: number;
    planned_permits: number;
    overdue_maintenance: number;
    active_alarms: number;
    online_cameras: number;
    total_cameras: number;
  };
  permit_types?: {
    access: number;
    work_authority: number;
    general_work: number;
  };
  recent_permits?: Array<{
    id: number;
    permit_number: string;
    permit_type: string;
    work_description: string;
    status: string;
    created_at: string;
  }>;
  available_areas?: Array<{
    id: number;
    area_name: string;
    image: string | null;
    width: number;
    height: number;
    is_active: boolean;
  }>;
  total_tags?: number;
  tags?: Array<{
    id: number;
    tag_name: string;
    current_value: number;
    current_value_display: string;
    alarm_status: string;
    alarms_enabled: boolean;
    description: string;
    engineering_unit: string;
    overview_position_x: number | null;
    overview_position_y: number | null;
    link_to_area: string | null;
    area_id: number | null;
    area_name: string | null;
    display_mode?: 'status_icon' | 'minimal_text' | 'compact_button' | 'value_only' | 'badge' | 'gauge';
  }>;
}

class SitesService {
  private apiCall = async (endpoint: string, options: RequestInit = {}) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || `HTTP ${response.status}`);
    }

    return response.json();
  };

  // Asset Owners
  async getAssetOwners(): Promise<AssetOwner[]> {
    const response = await this.apiCall('/sites/asset-owners/');
    return response.results || response;
  }

  async getAssetOwner(id: number): Promise<AssetOwner> {
    return this.apiCall(`/sites/asset-owners/${id}/`);
  }

  // Sites
  async getSites(): Promise<Site[]> {
    const response = await this.apiCall('/sites/');
    return response.results || response;
  }

  async getSite(id: number): Promise<Site> {
    return this.apiCall(`/sites/${id}/`);
  }

  async getSitesByAssetOwner(assetOwnerId: number): Promise<Site[]> {
    const response = await this.apiCall(`/sites/?asset_owner=${assetOwnerId}`);
    return response.results || response;
  }

  // Locations
  async getLocations(): Promise<Location[]> {
    const response = await this.apiCall('/sites/locations/');
    return response.results || response;
  }

  async getLocationsBySite(siteId: number): Promise<Location[]> {
    const response = await this.apiCall(`/sites/locations/?site=${siteId}`);
    return response.results || response;
  }

  async getLocation(id: number): Promise<Location> {
    return this.apiCall(`/sites/locations/${id}/`);
  }

  // Equipment
  async getEquipment(): Promise<Equipment[]> {
    const response = await this.apiCall('/sites/equipment/');
    return response.results || response;
  }

  async getEquipmentBySite(siteId: number): Promise<Equipment[]> {
    const response = await this.apiCall(`/sites/equipment/?site=${siteId}`);
    return response.results || response;
  }

  async getEquipmentItem(id: number): Promise<Equipment> {
    return this.apiCall(`/sites/equipment/${id}/`);
  }

  async searchEquipment(query: string): Promise<Equipment[]> {
    const response = await this.apiCall(`/sites/equipment/?search=${encodeURIComponent(query)}`);
    return response.results || response;
  }

  // Energy Sources
  async getEnergySourcesBySite(siteId: number): Promise<EnergySource[]> {
    const response = await this.apiCall(`/sites/energy-sources/?site=${siteId}`);
    return response.results || response;
  }

  async getEnergySourcesByEquipment(equipmentId: number): Promise<EnergySource[]> {
    const response = await this.apiCall(`/sites/energy-sources/?equipment=${equipmentId}`);
    return response.results || response;
  }

  // Isolation Points
  async getIsolationPointsByEquipment(equipmentId: number): Promise<IsolationPoint[]> {
    const response = await this.apiCall(`/sites/isolation-points/?equipment=${equipmentId}`);
    return response.results || response;
  }

  async getIsolationPointsByEnergySource(energySourceId: number): Promise<IsolationPoint[]> {
    const response = await this.apiCall(`/sites/isolation-points/?energy_source=${energySourceId}`);
    return response.results || response;
  }

  // Standard Tasks
  async getStandardTasks(filters?: { page?: number; page_size?: number; site_id?: string; task_type?: string; search?: string }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.page_size) params.append('page_size', filters.page_size.toString());
    if (filters?.site_id) params.append('site_id', filters.site_id);
    if (filters?.task_type) params.append('task_type', filters.task_type);
    if (filters?.search) params.append('search', filters.search);
    
    const queryString = params.toString();
    const endpoint = `/sites/standard-tasks/${queryString ? `?${queryString}` : ''}`;
    return await this.apiCall(endpoint);
  }

  async getStandardTask(id: number): Promise<any> {
    return await this.apiCall(`/sites/standard-tasks/${id}/`);
  }

  // TaskDataField methods (part of StandardTask consolidation)
  async getTaskDataFields(filters?: { standard_task?: number; input_type?: string; page?: number; page_size?: number }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.standard_task) params.append('standard_task', filters.standard_task.toString());
    if (filters?.input_type) params.append('input_type', filters.input_type);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.page_size) params.append('page_size', filters.page_size.toString());
    
    const queryString = params.toString();
    const endpoint = `/sites/task-data-fields/${queryString ? `?${queryString}` : ''}`;
    return await this.apiCall(endpoint);
  }

  async getTaskDataField(id: number): Promise<any> {
    return await this.apiCall(`/sites/task-data-fields/${id}/`);
  }

  async createTaskDataField(taskField: any): Promise<any> {
    return await this.apiCall(`/sites/task-data-fields/`, {
      method: 'POST',
      body: JSON.stringify(taskField),
    });
  }

  async updateTaskDataField(id: number, taskField: any): Promise<any> {
    return await this.apiCall(`/sites/task-data-fields/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(taskField),
    });
  }

  async deleteTaskDataField(id: number): Promise<void> {
    await this.apiCall(`/sites/task-data-fields/${id}/`, {
      method: 'DELETE',
    });
  }

  async getSiteDashboard(siteId: number): Promise<SiteDashboardData> {
    // TEMPORARILY DISABLED - Using lightweight endpoint for performance testing
    // return this.apiCall(`/sites/dashboard/?site_id=${siteId}`);
    return this.apiCall(`/sites/dashboard/lightweight/?site_id=${siteId}`);
  }

  // Get current values display configuration
  async getCurrentValuesConfig(siteId: number): Promise<{
    site_id: number;
    site_name: string;
    current_values_display_config: CurrentValuesDisplayConfig;
  }> {
    console.log(`Attempting to fetch current values config for site ${siteId}`);
    try {
      const result = await this.apiCall(`/sites/${siteId}/current-values-config/`);
      console.log('Current values config result:', result);
      return result;
    } catch (error) {
      console.error('Error fetching current values config:', error);
      throw error;
    }
  }

  // Update current values display configuration
  async updateCurrentValuesConfig(
    siteId: number, 
    config: CurrentValuesDisplayConfig
  ): Promise<{
    site_id: number;
    site_name: string;
    current_values_display_config: CurrentValuesDisplayConfig;
    message: string;
  }> {
    return this.apiCall(`/sites/${siteId}/current-values-config/`, {
      method: 'PUT',
      body: JSON.stringify({
        current_values_display_config: config
      }),
    });
  }
}

export const sitesService = new SitesService(); 